import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Scroll } from '@phosphor-icons/react';

const PrivacyPolicyModal = ({ darkMode, isOpen, onClose, onAccept, onDecline }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10000] flex items-end md:items-center justify-center p-0 md:p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: '100%', scale: 0.95 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: '100%', scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full md:max-w-5xl md:max-h-[90vh] h-full md:h-auto md:rounded-2xl overflow-hidden shadow-2xl ${
              darkMode 
                ? 'bg-slate-900 md:border md:border-white/10' 
                : 'bg-white md:border md:border-gray-200'
            }`}
          >
            {/* Header */}
            <div className={`sticky top-0 z-10 px-6 md:px-8 py-4 md:py-6 border-b flex items-center justify-between ${
              darkMode 
                ? 'bg-slate-800/95 border-white/10 backdrop-blur-lg' 
                : 'bg-white/95 border-gray-200 backdrop-blur-lg'
            }`}>
              <div className="flex items-center gap-3">
                <Scroll size={28} weight="duotone" className={darkMode ? 'text-blue-400' : 'text-blue-600'} />
                <h2 className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-white' : 'text-blue-950'}`}>
                  Privacy Policy & Data Protection
                </h2>
              </div>
              <button
                onClick={onClose}
                className={`p-2 rounded-lg transition-colors ${
                  darkMode 
                    ? 'hover:bg-white/10 text-gray-400 hover:text-white' 
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
                }`}
                aria-label="Close privacy policy"
              >
                <X size={24} weight="bold" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto h-[calc(100vh-180px)] md:max-h-[calc(90vh-180px)] px-6 md:px-8 py-6">
              <div className={`space-y-6 ${darkMode ? 'text-gray-200' : 'text-gray-700'} text-sm md:text-base`}>
                <p className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <strong>Effective Date:</strong> October 16, 2025 &nbsp;|&nbsp; <strong>Last Updated:</strong> October 16, 2025
                </p>

                {/* Introduction */}
                <section>
                  <h3 className={`text-lg md:text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-blue-950'}`}>
                    📋 Introduction
                  </h3>
                  <p className="mb-3">
                    Just One Computer Repair ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. 
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
                    or use our services, in compliance with the <strong>Data Privacy Act of 2012 (Republic Act No. 10173)</strong> of the Philippines.
                  </p>
                  <p>
                    By using our website and services, you consent to the data practices described in this policy.
                  </p>
                </section>

                {/* Information We Collect */}
                <section>
                  <h3 className={`text-lg md:text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-blue-950'}`}>
                    📝 Information We Collect
                  </h3>
                  
                  <h4 className={`font-semibold mb-2 mt-4 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                    Personal Information You Provide
                  </h4>
                  <ul className="list-disc list-inside space-y-1 mb-3 ml-4">
                    <li>Full name</li>
                    <li>Email address</li>
                    <li>Phone number</li>
                    <li>Service request details and messages</li>
                    <li>Device information (when you bring equipment for repair)</li>
                  </ul>

                  <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                    Automatically Collected Information
                  </h4>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Browser type and version</li>
                    <li>Device type and operating system</li>
                    <li>Dark mode preference (stored locally on your device)</li>
                  </ul>
                </section>

                {/* How We Use Your Information */}
                <section>
                  <h3 className={`text-lg md:text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-blue-950'}`}>
                    🎯 How We Use Your Information
                  </h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>To respond to your service inquiries and repair requests</li>
                    <li>To provide computer repair and technical support services</li>
                    <li>To communicate service updates, repair status, and confirmations</li>
                    <li>To improve our website and customer service experience</li>
                    <li>To comply with legal obligations and resolve disputes</li>
                  </ul>
                </section>

                {/* Legal Basis */}
                <section>
                  <h3 className={`text-lg md:text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-blue-950'}`}>
                    ⚖️ Legal Basis (Philippine Data Privacy Act)
                  </h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Consent:</strong> You have given explicit consent for processing your data</li>
                    <li><strong>Contract:</strong> Processing is necessary to fulfill our service agreement</li>
                    <li><strong>Legal Obligation:</strong> We must process data to comply with Philippine laws</li>
                    <li><strong>Legitimate Interest:</strong> Processing is necessary for our legitimate business operations</li>
                  </ul>
                </section>

                {/* Data Security */}
                <section>
                  <h3 className={`text-lg md:text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-blue-950'}`}>
                    🔒 Data Storage and Security
                  </h3>
                  <p className="mb-3">
                    We implement appropriate technical and organizational security measures:
                  </p>
                  <ul className="list-disc list-inside space-y-1 mb-3 ml-4">
                    <li>SSL/TLS encryption for data transmission</li>
                    <li>Secure email systems with encryption</li>
                    <li>Access controls and authentication mechanisms</li>
                    <li>Regular security audits and updates</li>
                  </ul>
                  <p>
                    Your contact form data is transmitted securely via EmailJS and stored only as long as necessary to provide our services. 
                    We retain personal data for a maximum of <strong>2 years</strong> unless longer retention is required by law.
                  </p>
                </section>

                {/* Third-Party Services */}
                <section>
                  <h3 className={`text-lg md:text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-blue-950'}`}>
                    🤝 Third-Party Services
                  </h3>
                  <p className="mb-3">We use the following trusted third-party services:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>EmailJS:</strong> For secure contact form submissions</li>
                    <li><strong>Google Fonts:</strong> For website typography (no personal data collected)</li>
                    <li><strong>Cloudflare:</strong> For secure website hosting</li>
                  </ul>
                  <p className={`mt-4 px-4 py-3 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} font-semibold`}>
                    ⚠️ We do NOT sell, rent, or trade your personal information to third parties.
                  </p>
                </section>

                {/* Cookies */}
                <section>
                  <h3 className={`text-lg md:text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-blue-950'}`}>
                    🍪 Cookies and Local Storage
                  </h3>
                  <p className="mb-3">
                    We use minimal browser storage technologies:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>localStorage:</strong> To save your dark mode preference (stored on your device only)</li>
                    <li><strong>No tracking cookies:</strong> We do not use cookies to track your behavior</li>
                    <li><strong>No analytics cookies:</strong> We do not collect analytics data without consent</li>
                  </ul>
                </section>

                {/* Your Rights */}
                <section>
                  <h3 className={`text-lg md:text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-blue-950'}`}>
                    ✅ Your Rights (Data Privacy Act of 2012)
                  </h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Right to be Informed:</strong> Know how your data is being processed</li>
                    <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
                    <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
                    <li><strong>Right to Erasure:</strong> Request deletion of your data</li>
                    <li><strong>Right to Data Portability:</strong> Request your data in a portable format</li>
                    <li><strong>Right to Object:</strong> Object to certain types of data processing</li>
                    <li><strong>Right to File a Complaint:</strong> File with the National Privacy Commission (NPC)</li>
                  </ul>
                  <p className="mt-3">
                    To exercise any of these rights, contact us at: <strong className={darkMode ? 'text-blue-400' : 'text-blue-600'}>0920 230 8881</strong>
                  </p>
                </section>

                {/* Children's Privacy */}
                <section>
                  <h3 className={`text-lg md:text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-blue-950'}`}>
                    👶 Children's Privacy
                  </h3>
                  <p>
                    Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal 
                    information from children. If you are a parent or guardian and believe we have collected information about 
                    a minor, please contact us immediately.
                  </p>
                </section>

                {/* Contact */}
                <section>
                  <h3 className={`text-lg md:text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-blue-950'}`}>
                    📞 Contact Us
                  </h3>
                  <p className="mb-3">
                    For privacy-related questions or to exercise your rights:
                  </p>
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900/30 border border-blue-700/30' : 'bg-blue-50 border border-blue-200'}`}>
                    <p className="font-semibold text-lg">Just One Computer Repair</p>
                    <p className="mt-2">Phone: <a href="tel:+639202308881" className={`font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>0920 230 8881</a></p>
                    <p>Location: Rosario, Batangas, Philippines</p>
                  </div>
                </section>

                {/* NPC */}
                <section>
                  <h3 className={`text-lg md:text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-blue-950'}`}>
                    🏛️ Filing a Complaint
                  </h3>
                  <p className="mb-3">
                    If you believe your data privacy rights have been violated, you may file a complaint with:
                  </p>
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                    <p className="font-semibold">National Privacy Commission (NPC)</p>
                    <p className="text-sm mt-2">5th Floor, Philippine International Convention Center (PICC)</p>
                    <p className="text-sm">Vicente Sotto St., Pasay City, Metro Manila 1307</p>
                    <p className="text-sm mt-1">Email: info@privacy.gov.ph</p>
                    <p className="text-sm">Website: <a href="https://www.privacy.gov.ph" target="_blank" rel="noopener noreferrer" className={darkMode ? 'text-blue-400' : 'text-blue-600'}>www.privacy.gov.ph</a></p>
                  </div>
                </section>
              </div>
            </div>

            {/* Footer Actions */}
            <div className={`sticky bottom-0 px-6 md:px-8 py-4 md:py-6 border-t flex flex-col sm:flex-row items-center justify-end gap-3 ${
              darkMode 
                ? 'bg-slate-800/95 border-white/10 backdrop-blur-lg' 
                : 'bg-white/95 border-gray-200 backdrop-blur-lg'
            }`}>
              <button
                onClick={onDecline}
                className={`w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition-all ${
                  darkMode
                    ? 'text-gray-300 hover:text-white hover:bg-white/10'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Decline All
              </button>
              <button
                onClick={onAccept}
                className={`w-full sm:w-auto px-8 py-3 rounded-lg font-semibold transition-all transform hover:scale-105 ${
                  darkMode
                    ? 'bg-blue-600 hover:bg-blue-500 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                } shadow-lg`}
              >
                Accept & Continue
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PrivacyPolicyModal;
